<template>
  <div class="posts-page">
    <section class="admin-panel posts-toolbar">
      <div>
        <p class="eyebrow">文章管理</p>
        <h2>文章记录</h2>
      </div>
      <div class="toolbar-actions">
        <el-input
          v-model.trim="keywordInput"
          clearable
          placeholder="搜索文章标题"
          size="small"
          @keyup.enter.native="searchArticles"
        />
        <el-button size="small" type="primary" @click="searchArticles">搜索</el-button>
      </div>
    </section>

    <section class="admin-panel posts-list-panel">
      <div class="table-status">
        <span>{{ loading ? '正在加载文章...' : `共 ${total} 篇文章` }}</span>
        <div class="table-status-actions">
          <span v-if="selectedPosts.length" class="selection-count">
            已选 {{ selectedPosts.length }} 篇
          </span>
          <el-button
            size="mini"
            type="danger"
            plain
            :disabled="selectedPosts.length === 0"
            :loading="batchDeleting"
            @click="removeSelectedPosts"
          >
            批量删除
          </el-button>
          <el-button size="mini" @click="refreshPosts">刷新</el-button>
        </div>
      </div>

      <el-table
        v-loading="loading"
        class="admin-data-table article-table"
        :data="posts"
        empty-text="暂无匹配文章"
        stripe
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="48" />
        <el-table-column label="封面" width="86">
          <template slot-scope="{ row }">
            <img class="admin-table-cover" :src="row.coverImage" :alt="`${row.title}封面`">
          </template>
        </el-table-column>
        <el-table-column label="标题" min-width="220" prop="title" show-overflow-tooltip />
        <el-table-column label="摘要" min-width="220" prop="summary" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.summary || '-' }}</template>
        </el-table-column>
        <el-table-column label="作者" prop="author" width="90">
          <template slot-scope="{ row }">{{ row.author || '-' }}</template>
        </el-table-column>
        <el-table-column label="分类" prop="categoryName" width="90">
          <template slot-scope="{ row }">{{ row.categoryName || '-' }}</template>
        </el-table-column>
        <el-table-column label="前台可见" width="130">
          <template slot-scope="{ row }">
            <div class="visibility-cell">
              <el-switch
                :value="row.visible"
                :disabled="Boolean(visibilityUpdating[row.id])"
                @change="toggleVisibility(row, $event)"
              />
              <span :class="['visibility-text', row.visible ? 'is-visible' : 'is-hidden']">
                {{ row.visible ? '可见' : '隐藏' }}
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" prop="updatedAt" width="190" show-overflow-tooltip />
        <el-table-column label="操作" width="120">
          <template slot-scope="{ row }">
            <router-link
              class="table-action-link"
              :to="`/admin/articles/${row.id}/edit`"
              @click.native="trackPostAction('点击编辑文章', row)"
            >
              编辑
            </router-link>
            <button class="table-action-danger" type="button" @click="removePost(row)">删除</button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-bar">
        <el-pagination
          background
          :current-page="page"
          layout="total, sizes, prev, pager, next"
          :page-sizes="[5, 10, 20, 50]"
          :page-size="size"
          :total="total"
          @current-change="changePage"
          @size-change="onSizeChange"
        />
      </div>
    </section>
  </div>
</template>

<script>
import {
  batchDeleteAdminPosts,
  deleteAdminPost,
  fetchAdminPostDetail,
  fetchAdminPosts,
  updateAdminPost,
} from '@/services/posts';
import { getArticleCover } from '@/utils/articles';
import { trackFrontendEvent } from '@/services/operationLogs';

export default {
  name: 'AdminPosts',
  data() {
    return {
      keyword: '',
      keywordInput: '',
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      batchDeleting: false,
      posts: [],
      selectedPosts: [],
      visibilityUpdating: {},
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.size));
    },
  },
  created() {
    this.loadPosts();
  },
  methods: {
    trackOperation(operation, detail, success = true) {
      trackFrontendEvent({
        operation,
        pagePath: this.$route.path,
        detail,
        success,
      });
    },
    normalizePost(item) {
      return {
        id: item.id || item.postId,
        title: item.title || '',
        summary: item.summary || item.description || '',
        author: item.author || '',
        coverImage: getArticleCover(item.coverImage),
        content: item.content || '',
        updatedAt: item.publishedAt || item.updatedAt || item.updateTime || item.createdAt || '-',
        visible: item.visible !== false,
        categoryName: item.category?.name || '',
      };
    },
    normalizeArticleDetail(body) {
      const article = body?.data || body || {};

      return {
        title: article.title || '',
        summary: article.summary || article.description || '',
        contentFormat: article.contentFormat || 'MARKDOWN',
        content: article.content || '',
        visible: article.visible !== false,
        categoryId: article.category?.id || article.categoryId || null,
      };
    },
    extractPosts(body) {
      const list = Array.isArray(body)
        ? body
        : body?.data?.records || body?.data?.list || body?.data || body?.records || body?.list;

      return Array.isArray(list) ? list.map(this.normalizePost) : null;
    },
    extractTotal(body, fallback) {
      if (!body) return fallback;
      const data = body.data || body;
      return data?.total
        || data?.pages
        || body?.total
        || data?.count
        || body?.count
        || data?.totalElements
        || fallback;
    },
    async loadPosts() {
      this.loading = true;

      try {
        const params = {
          page: this.page,
          size: this.size,
          keyword: this.keyword,
        };
        if (this.categoryId) params.categoryId = this.categoryId;
        const response = await fetchAdminPosts(params);
        const posts = this.extractPosts(response.data);
        this.posts = posts || [];
        this.total = this.extractTotal(response.data, this.posts.length);
        this.selectedPosts = [];
      } catch (error) {
        this.posts = [];
        this.total = 0;
        this.$message.error(error.response?.data?.message || '暂时无法读取文章列表');
      } finally {
        this.loading = false;
      }
    },
    searchArticles() {
      this.keyword = this.keywordInput;
      this.page = 1;
      this.trackOperation('搜索文章', `关键词：${this.keyword || '空'}`);
      this.loadPosts();
    },
    refreshPosts() {
      this.trackOperation('刷新文章列表', '用户点击文章列表刷新按钮');
      this.loadPosts();
    },
    changePage(page) {
      this.page = page;
      this.loadPosts();
    },
    onSizeChange(size) {
      this.size = size;
      this.page = 1;
      this.loadPosts();
    },
    handleSelectionChange(selection) {
      this.selectedPosts = selection;
    },
    async removePost(post) {
      try {
        await this.$confirm(`确定删除《${post.title}》吗？`, '删除文章确认', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
        });
      } catch (error) {
        return;
      }

      try {
        await deleteAdminPost(post.id);
        this.trackOperation('删除文章', `文章ID：${post.id || '-'}，标题：${post.title || '-'}`);
        await this.loadPosts();
      } catch (error) {
        this.trackOperation('删除文章失败', `文章ID：${post.id || '-'}，原因：${error.response?.data?.message || error.message || '未知错误'}`, false);
        this.$message.error(error.response?.data?.message || '删除失败，请稍后再试');
      }
    },
    async removeSelectedPosts() {
      const ids = this.selectedPosts.map((post) => post.id).filter(Boolean);

      if (ids.length === 0) {
        return;
      }

      try {
        await this.$confirm(`确定删除选中的 ${ids.length} 篇文章吗？`, '批量删除确认', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
        });
      } catch (error) {
        return;
      }

      this.batchDeleting = true;

      try {
        await batchDeleteAdminPosts(ids);
        this.selectedPosts = [];
        this.trackOperation('批量删除文章', `用户点击批量删除按钮，删除 ${ids.length} 篇文章`);
        this.$message.success('批量删除成功');
        await this.loadPosts();
      } catch (error) {
        this.trackOperation('批量删除文章失败', `文章ID：${ids.join('、')}，原因：${error.response?.data?.message || error.message || '未知错误'}`, false);
        this.$message.error(error.response?.data?.message
          || error.response?.data?.msg
          || '批量删除失败，请稍后再试');
      } finally {
        this.batchDeleting = false;
      }
    },
    async toggleVisibility(post, nextVisible) {
      const previousVisible = post.visible;

      this.$set(this.visibilityUpdating, post.id, true);
      this.$set(post, 'visible', nextVisible);

      try {
        const response = await fetchAdminPostDetail(post.id);
        const detail = this.normalizeArticleDetail(response.data);

        await updateAdminPost(post.id, {
          ...detail,
          visible: nextVisible,
        });
        this.trackOperation(
          '修改文章可见状态',
          `文章ID：${post.id || '-'}，标题：${post.title || '-'}，状态：${nextVisible ? '可见' : '隐藏'}`,
        );
      } catch (error) {
        this.$set(post, 'visible', previousVisible);
        this.trackOperation(
          '修改文章可见状态失败',
          `文章ID：${post.id || '-'}，原因：${error.response?.data?.message || error.message || '未知错误'}`,
          false,
        );
        this.$message.error(error.response?.data?.message
          || error.response?.data?.msg
          || '可见状态更新失败');
      } finally {
        this.$delete(this.visibilityUpdating, post.id);
      }
    },
  },
};
</script>
